import React from 'react';

interface SparklineProps {
  readonly values: ReadonlyArray<number>;
  readonly width?: number;
  readonly height?: number;
  readonly maxValue?: number;
  readonly strokeColor?: string;
  readonly baselineColor?: string;
  readonly strokeWidth?: number;
  readonly ariaLabel: string;
  readonly className?: string;
}

const Sparkline: React.FC<SparklineProps> = ({
  values,
  width = 140,
  height = 36,
  maxValue,
  strokeColor = '#4ecdc4',
  baselineColor = 'rgba(78, 205, 196, 0.35)',
  strokeWidth = 2,
  ariaLabel,
  className,
}) => {
  const sanitizedValues = values.length > 0 ? [...values] : [0];
  const effectiveMax = Math.max(0, maxValue ?? Math.max(...sanitizedValues, 0));

  const points = sanitizedValues.map((rawValue, index) => {
    const clampedValue = Math.max(0, rawValue);
    const x =
      sanitizedValues.length === 1
        ? width / 2
        : (index / (sanitizedValues.length - 1)) * width;
    const normalized = effectiveMax > 0 ? clampedValue / effectiveMax : 0;
    const y = height - normalized * height;

    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  const shouldRenderBaseline = effectiveMax === 0;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio='none'
      role='img'
      aria-label={ariaLabel}
    >
      {shouldRenderBaseline ? (
        <line
          x1='0'
          y1={height}
          x2={width}
          y2={height}
          stroke={baselineColor}
          strokeWidth={strokeWidth}
        />
      ) : (
        <polyline
          points={points.join(' ')}
          fill='none'
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      )}
    </svg>
  );
};

export { Sparkline };
